import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    FaRegBell,
    FaBroadcastTower,
    FaEye,
    FaSearch,
    FaInbox,
    FaBullhorn,
    FaHistory,
    FaTimes,
    FaExclamationTriangle,
    FaInfoCircle,
    FaCheckCircle,
    FaTimesCircle,
    FaTrashAlt,
    FaCheckDouble
} from 'react-icons/fa';
import { showSuccess, showError, showLoading, closeLoading } from '../../../utils/Alert';
import Loader from '../../shared/Loader';
import './RecruiterNotifications.css';

const RecruiterNotifications = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [notifications, setNotifications] = useState([]);
    const [activeTab, setActiveTab] = useState('alerts'); // 'alerts', 'messages', 'history'
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchNotifications();
    }, []);

    const fetchNotifications = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/notifications', {
                headers: { Authorization: `Bearer ${token}` }
            });
            // Isolation: Recruiter only sees notifications for 'All', 'recruiters', or their specific target
            setNotifications(res.data);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching notifications", err);
            setLoading(false);
        }
    };

    const markAsRead = async (id, skipStateUpdate = false) => {
        try {
            const token = localStorage.getItem('token');
            await axios.patch(`/api/notifications/${id}/read`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!skipStateUpdate) {
                const updated = notifications.filter(n => n._id !== id);
                setNotifications(updated);

                // Sync with header count
                const newUnread = updated.filter(n => !n.isRead).length;
                window.dispatchEvent(new CustomEvent('notificationsRead', { detail: { count: newUnread } }));
            }
        } catch (err) {
            console.error("Could not dismiss notification", err);
            if (!skipStateUpdate) showError("Action Failed", "Could not dismiss notification.");
        }
    };

    const handleNotificationClick = async (notif) => {
        // Mark as read first
        await markAsRead(notif._id, true);

        // Keywords for redirection
        const title = notif.title.toLowerCase();
        const msg = notif.message.toLowerCase();

        let targetPath = '';

        if (title.includes('job') || title.includes('listing') || msg.includes('job position')) {
            targetPath = '/recruiter/jobs';
        } else if (title.includes('applicant') || title.includes('candidate') || title.includes('sourcing') || msg.includes('applied')) {
            targetPath = '/recruiter/applicants';
        } else if (title.includes('interview') || title.includes('scheduled') || msg.includes('interview')) {
            targetPath = '/recruiter/interviews';
        } else if (title.includes('company') || title.includes('branding') || title.includes('logo')) {
            targetPath = '/recruiter/company';
        } else if (title.includes('message') || title.includes('chat') || msg.includes('replied')) {
            targetPath = '/recruiter/messages';
        } else if (title.includes('report') || title.includes('analytic') || title.includes('stat')) {
            targetPath = '/recruiter/reports';
        } else if (title.includes('profile') || title.includes('account')) {
            targetPath = '/recruiter/profile';
        }

        // Global/Announcements usually don't have a specific section target, stay here or go to dashboard
        if (notif.target === 'All' || notif.type === 'Alert' || !targetPath) {
            // Just refresh list to remove the read notification
            const updated = notifications.filter(n => n._id !== notif._id);
            setNotifications(updated);
            const newUnread = updated.filter(n => !n.isRead).length;
            window.dispatchEvent(new CustomEvent('notificationsRead', { detail: { count: newUnread } }));
            return;
        }

        if (targetPath) {
            navigate(targetPath);
        }
    };

    const markAllRead = async () => {
        try {
            showLoading("Clearing all notifications...");
            const token = localStorage.getItem('token');
            await axios.patch('/api/notifications/mark-all-read', {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotifications([]);

            // Sync with header count
            window.dispatchEvent(new CustomEvent('notificationsRead', { detail: { count: 0 } }));

            closeLoading();
            showSuccess("Success", "All notifications dismissed.");
        } catch (err) {
            closeLoading();
            showError("Action Failed", "Could not clear hub.");
        }
    };

    const filteredNotifications = notifications.filter(n => {
        const matchesSearch = n.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            n.message.toLowerCase().includes(searchTerm.toLowerCase());

        if (activeTab === 'alerts') return matchesSearch && (n.type === 'Alert' || n.target === 'All' || n.target === 'recruiters');
        if (activeTab === 'messages') return matchesSearch && (n.type === 'Info' || n.type === 'Success');
        return matchesSearch;
    });

    const getIcon = (type) => {
        switch (type) {
            case 'Alert': return <FaExclamationTriangle className="type-icon alert" />;
            case 'Success': return <FaCheckCircle className="type-icon success" />;
            case 'Warning': return <FaTimesCircle className="type-icon warning" />;
            default: return <FaInfoCircle className="type-icon info" />;
        }
    };

    if (loading) return <Loader />;

    return (
        <div className="recruiter-notifications-wrapper fade-in">
            <div className="hub-header">
                <div className="header-info">
                    <h1 className="hub-title"><FaRegBell /> Recruiter Intelligence Hub</h1>
                    <p className="hub-subtitle">Centralized updates on applicant movements, system shifts, and institutional alerts.</p>
                </div>
                <div className="header-actions">
                    <button className="clear-all-btn" onClick={markAllRead} disabled={notifications.length === 0}>
                        <FaCheckDouble /> Mark All as Read
                    </button>
                </div>
            </div>

            <div className="hub-controls">
                <div className="tab-filters">
                    <button
                        className={`tab-btn ${activeTab === 'alerts' ? 'active' : ''}`}
                        onClick={() => setActiveTab('alerts')}
                    >
                        <FaBullhorn /> Institutional Alerts
                        {notifications.filter(n => (n.type === 'Alert' || n.target === 'All' || n.target === 'recruiters')).length > 0 && (
                            <span className="tab-badge">{notifications.filter(n => (n.type === 'Alert' || n.target === 'All' || n.target === 'recruiters')).length}</span>
                        )}
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'messages' ? 'active' : ''}`}
                        onClick={() => setActiveTab('messages')}
                    >
                        <FaInbox /> Hiring Signals
                        {notifications.filter(n => (n.type === 'Info' || n.type === 'Success')).length > 0 && (
                            <span className="tab-badge">{notifications.filter(n => (n.type === 'Info' || n.type === 'Success')).length}</span>
                        )}
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
                        onClick={() => setActiveTab('history')}
                    >
                        <FaHistory /> Archived Updates
                    </button>
                </div>

                <div className="search-bar">
                    <FaSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search intelligence..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="notifications-list">
                {filteredNotifications.length > 0 ? (
                    filteredNotifications.map((notif) => (
                        <div key={notif._id} className={`notif-card ${notif.type.toLowerCase()}`} onClick={() => handleNotificationClick(notif)} style={{ cursor: 'pointer' }}>
                            <div className="notif-icon-box">
                                {getIcon(notif.type)}
                            </div>
                            <div className="notif-content">
                                <div className="notif-meta">
                                    <span className={`notif-badge ${notif.type.toLowerCase()}`}>{notif.type}</span>
                                    <span className="notif-date">{new Date(notif.createdAt).toLocaleString()}</span>
                                </div>
                                <h3 className="notif-title">{notif.title}</h3>
                                <p className="notif-message">{notif.message}</p>
                            </div>
                            <button className="dismiss-btn" onClick={(e) => { e.stopPropagation(); markAsRead(notif._id); }} title="Clear Notification">
                                <FaTimes />
                            </button>
                        </div>
                    ))
                ) : (
                    <div className="empty-hub">
                        <div className="empty-icon-wrapper">
                            <FaRegBell />
                        </div>
                        <h3>Intelligence Hub Clear</h3>
                        <p>No new updates in this sector at the moment.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RecruiterNotifications;
